import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { ValidationRule } from '@mayksel/types';
import type {
  CreateValidationRuleInput,
  UpdateValidationRuleInput,
} from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useValidationRules(entityType?: string) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<ValidationRule[]>({
    queryKey: ['validation-rules', workspaceId, entityType],
    queryFn: async () => {
      const token = await getToken();
      const qs = entityType ? `?entityType=${entityType}` : '';
      return api.get<ValidationRule[]>(`/v1/validation-rules${qs}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useCreateValidationRule() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateValidationRuleInput) => {
      const token = await getToken();
      return api.post<ValidationRule>('/v1/validation-rules', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['validation-rules', workspaceId] });
    },
  });
}

export function useUpdateValidationRule() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateValidationRuleInput }) => {
      const token = await getToken();
      return api.patch<ValidationRule>(`/v1/validation-rules/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['validation-rules', workspaceId] });
    },
  });
}

export function useDeleteValidationRule() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<null>(`/v1/validation-rules/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['validation-rules', workspaceId] });
    },
  });
}
